/* eslint-disable i18next/no-literal-string,@typescript-eslint/naming-convention,camelcase */
import _noop from 'lodash/noop';
import { createSegmentClient } from '../clients';
import { AnalyticsProduct, EventTracker } from '../events';

export const createSegmentTracker = (): EventTracker => {
  const client = createSegmentClient();

  return {
    // Order events
    placedOrder: (payload, context) => {
      void client.track('Order Completed', {
        checkout_id: payload.checkoutToken, // deprecated: use checkout_token
        checkout_token: payload.checkoutToken,
        delivery_method: payload.order.deliveryInfo?.deliveryOption ? 'delivery' : 'pickup',
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        dutchie_pay_enabled: payload.isDutchiePayEnabledForDispo,
        order_id: payload.order.orderId,
        payment_method: payload.order.paymentMethod ?? null,
        products: context.activeOrder?.orders.map((o) => ({
          ...toSegmentProduct(o.product ?? null),
          price: o.price,
          quantity: o.quantity,
        })),
        purchase_type: payload.order.medicalOrder ? 'medicinal' : 'recreational',
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
      });
    },

    // Checkout events
    checkoutStarted: (payload) => {
      void client.track('Checkout Started', {
        total: payload.total,
        revenue: payload.revenue,
        tax: payload.tax,
        discount: payload.discount,
        currency: payload.currency,
        products: payload.products,
      });
    },
    viewedCheckout: (payload, context) => {
      void client.track('Checkout Step Viewed', {
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        step: 1,
        delivery_method: null,
        payment_method: null,
        dutchie_pay_enabled: payload.isDutchiePayEnabledForDispo,
        user_is_guest: Boolean(!context.activeUser?.id),
        user_enrolled_dutchie_pay: payload.isEnrolledDutchiePay,
      });
    },

    // DutchiePay Enrollment
    dutchiePayEnrollmentButtonClicked(_, context) {
      void client.track(`DutchiePay Enrollment Button Clicked`, {
        active_path: context.activePath?.normalized ?? null,
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        user_is_guest: Boolean(!context.activeUser?.id),
      });
    },

    dutchiePayLearnMoreButtonClicked(_, context) {
      void client.track(`DutchiePay Learn More Button Clicked`, {
        active_path: context.activePath?.normalized ?? null,
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        user_is_guest: Boolean(!context.activeUser?.id),
      });
    },

    dutchiePayCTAButtonClicked(_, context) {
      void client.track(`DutchiePay CTA Button Clicked`, {
        active_path: context.activePath?.normalized ?? null,
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        user_is_guest: Boolean(!context.activeUser?.id),
      });
    },

    dutchiePayEnrollmentStep(payload, context) {
      void client.track(`DutchiePay Enrollment Step ${payload.action}`, {
        active_path: context.activePath?.normalized ?? null,
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        source: payload.source ?? null,
        step: payload.step,
      });
    },

    dutchiePayConnectBankStep(payload, context) {
      void client.track(`DutchiePay MX Link ${payload.action}`, {
        active_path: context.activePath?.normalized ?? null,
        checkout_token: context.checkoutToken ?? null,
        dispensary_id: context.activeDispensary?.id ?? null,
        dispensary_name: context.activeDispensary?.name ?? null,
        source: payload.source ?? null,
      });
    },

    // Noop events
    addedProductToCart: _noop,
    removedProductFromCart: _noop,
    searchedForProduct: _noop,
    searchResults: _noop,
    cartViewed: _noop,
    viewedProduct: _noop,
    productClicked: _noop,
  };
};

type SegmentProduct = {
  brand: string | null;
  category: string | null;
  image_url: string | null;
  name: string | null;
  product_id: string | null;
  sku: string | null;
  url: string | null;
  variant: string | null;
  price: number | null;
};

const toSegmentProduct = (product: AnalyticsProduct | null): SegmentProduct => ({
  // the weird casting is because `brand` doesn't exist on all possible product Union types
  brand: (product as (AnalyticsProduct & Record<string, any>) | null)?.brand?.name ?? null,
  category: product?.type ?? null,
  image_url: product?.Image ?? null,
  name: product?.Name ?? null,
  price: product?.Prices?.length === 1 ? product.Prices[0] : null,
  product_id: (product as (AnalyticsProduct & Record<string, any>) | null)?.id ?? null,
  sku: null,
  variant: product?.Options?.join(', ') ?? null,
  url: null,
});
